import { toast } from 'react-toastify';
import { WEEKDAYS } from '../components/protocols/ProtocolDaysEditor';

export const LANG_SECTIONS = [
  { key: 'sr', label: 'Srpski' },
  { key: 'hu', label: 'Mađarski' },
  { key: 'en', label: 'Engleski' },
];

export const DURATION_OPTIONS = [
  { value: 7, label: '7 dana' },
  { value: 30, label: '30 dana' },
];

export const WEIGHT_RANGE_OPTIONS = [
  { value: '10_20', label: '10-20 kg' },
  { value: '21_30', label: '21-30 kg' },
  { value: '31_40', label: '31-40 kg' },
  { value: '41_50', label: '41-50 kg' },
  { value: '51_60', label: '51-60 kg' },
  { value: '61_70', label: '61-70 kg' },
  { value: '71_80', label: '71-80 kg' },
  { value: '81_90', label: '81-90 kg' },
  { value: '91_100', label: '91-100 kg' },
  { value: 'gt_100', label: '100+ kg' },
];

// Same codes as the backend's PROTOCOL_CATEGORIES. Labels are replaced by
// GET /protocols/categories (useProtocolCategories) so the dashboard shows
// exactly what the app shows; this list is only the offline fallback.
export const PROTOCOL_CATEGORY_OPTIONS = [
  { value: 'diseases', label: 'Bolesti' },
  { value: 'lifestyle', label: 'Životni stil' },
  { value: 'sports_activity', label: 'Sport i aktivnost' },
  { value: 'longevity', label: 'Dugovečnost' },
];

// Every protocol category's items live in /protocol-subcategories; this is
// the tab key the protocols page uses for each (and the form's dropdown order).
export const SUBCATEGORY_TABS = [
  { category: 'diseases', tab: 'bolesti' },
  { category: 'lifestyle', tab: 'stil-zivota' },
  { category: 'sports_activity', tab: 'sport' },
  { category: 'longevity', tab: 'dugovecnost' },
];

export const tabForSubcategoryCategory = (category) =>
  SUBCATEGORY_TABS.find((t) => t.category === category)?.tab || 'protokoli';

// Each tag implies its category; the backend enforces the same rule.
export const withCategory = (categories, category) =>
  categories.includes(category) ? categories : [...categories, category];

// The subcategory id picked for `category`, out of the form's flat id list.
export const subcategoryFor = (ids, category, subcategoryOptions) =>
  (ids || []).find(
    (id) => subcategoryOptions.find((o) => o.value === id)?.category === category,
  ) || null;

// Drops the tags whose category is no longer selected. Ids missing from
// `subcategoryOptions` (not loaded) are kept rather than silently removed.
export const tagsForCategories = (formData, categories, subcategoryOptions) =>
  (formData.subcategories || []).filter((id) => {
    const option = subcategoryOptions.find((o) => o.value === id);
    return !option || categories.includes(option.category);
  });

export const subcategoryLabels = (ids, subcategoryOptions) =>
  (ids || [])
    .map((id) => subcategoryOptions.find((o) => o.value === id)?.label)
    .filter(Boolean)
    .join(', ') || '-';

export const labelsFor = (values, options) =>
  (values || [])
    .map((value) => options.find((o) => o.value === value)?.label || value)
    .join(', ') || '-';

export const EMPTY_PROTOCOL_FORM = {
  subcategories: [],
  categories: [],
  rank: null,
  name: { sr: '', hu: '', en: '' },
  imageUrl: '',
  durationDays: null,
  weightRanges: [],
  versionNotes: '',
  days: [],
};

// Protocols saved before activity ordering have `order: 0` everywhere, so
// fall back to time — the same rule the backend uses.
const byOrderThenTime = (a, b) =>
  (a.order || 0) - (b.order || 0) || (a.time || '').localeCompare(b.time || '');

// Request shape for `days`: an activity's position in the editor is its order.
export const toDaysPayload = (days) =>
  days
    .filter((day) => day.activities.length > 0)
    .map((day) => ({
      dayOfWeek: day.dayOfWeek,
      activities: day.activities.map((activity, index) => ({
        category: activity.category,
        name: activity.name,
        content: activity.content,
        dosage: activity.dosage || undefined,
        imageUrls: activity.imageUrls || [],
        time: activity.time,
        order: index + 1,
      })),
    }));

// Maps a saved protocol (the admin detail response) onto the wizard's form
// shape. Shared by the edit page and by duplication, so the two can't drift.
export const protocolToFormData = (protocol) => ({
  subcategories: protocol.subcategories || [],
  categories: protocol.categories || [],
  rank: protocol.rank ?? null,
  name: protocol.name || { sr: '', hu: '', en: '' },
  imageUrl: protocol.imageUrl || '',
  durationDays: protocol.durationDays,
  weightRanges: protocol.weightRanges || [],
  versionNotes: '',
  days: (protocol.days || []).map((day) => ({
    ...day,
    activities: [...day.activities].sort(byOrderThenTime),
  })),
});

const COPY_SUFFIX = { sr: '(kopija)', hu: '(másolat)', en: '(copy)' };

// A duplicate is a brand-new lineage (version 1), not a new version of the
// original, so it goes through the create form: `durationDays` and
// `weightRanges` can only be set there, and wanting a different one of those is
// usually the whole reason for duplicating.
export const protocolToDuplicateFormData = (protocol) => {
  const form = protocolToFormData(protocol);
  return {
    ...form,
    name: Object.fromEntries(
      LANG_SECTIONS.map(({ key }) => [
        key,
        form.name[key] ? `${form.name[key]} ${COPY_SUFFIX[key]}` : '',
      ])
    ),
  };
};

// `mode==='edit'` skips durationDays/weightRanges — they're pre-set and
// disabled, UpdateProtocolDto doesn't accept them (a protocol's lineage
// identity can't change via edit, only via creating a new protocol).
// Subcategories (diseases included) are optional tags; categories are required in both modes.
export const validateBasicInfo = (formData, mode) => {
  if (!formData.categories?.length) {
    toast.error('Izaberite bar jednu kategoriju');
    return false;
  }
  for (const { key, label } of LANG_SECTIONS) {
    if (!formData.name[key]?.trim()) {
      toast.error(`Naziv na ${label}om je obavezan`);
      return false;
    }
  }
  if (mode === 'create' && !formData.durationDays) {
    toast.error('Trajanje programa je obavezno');
    return false;
  }
  if (mode === 'create' && !formData.weightRanges?.length) {
    toast.error('Izaberite bar jedan opseg težine');
    return false;
  }
  return true;
};

export const validateActivities = (formData) => {
  if (!formData.days.length) {
    toast.error('Dani programa nisu definisani');
    return false;
  }
  for (const day of formData.days) {
    const weekdayLabel = WEEKDAYS.find((w) => w.key === day.dayOfWeek)?.label || day.dayOfWeek;
    for (const activity of day.activities) {
      if (!activity.category) {
        toast.error(`${weekdayLabel}: kategorija aktivnosti je obavezna`);
        return false;
      }
      if (!activity.time) {
        toast.error(`${weekdayLabel}: vreme aktivnosti je obavezno`);
        return false;
      }
      for (const { key, label } of LANG_SECTIONS) {
        if (!activity.name?.[key]?.trim()) {
          toast.error(`${weekdayLabel}: naziv aktivnosti na ${label}om je obavezan`);
          return false;
        }
      }
    }
  }
  return true;
};
